// Payroll runs: what a pull from AxisCare writes.
//
// The page's "Pull from AxisCare" and the Monday job (`open-payroll-runs`)
// both write through here. Every function takes the Supabase client as its
// first argument, so the same code runs in the browser and in an edge
// function, and the writes can be pinned in tests. How visits become lines is
// lib/payroll/payrollLines.js.

import { must } from '../billing/must.js';
import { buildPayrollLines, payrollTotals } from './payrollLines.js';

const STAFF_SELECT = 'id, axiscare_id, full_name, position_code, pay_rate, pay_type, employment_type, status, start_date, end_date, termination_date, concurrent_care_approved, contracting_agency_id, contracting_agency:contracting_agencies(id, name)';

/**
 * What a pull needs besides the visits. Staff of every status: a caregiver
 * who has since left still worked the visit and is still owed for it — until
 * 2026-10-28 only active staff were read, so their hours landed with no rate.
 */
export const loadPullInputs = async (sb, runId) => {
  const [staffRows, clientRows, existing, rateHistory, catalog] = await Promise.all([
    must(sb.from('staff').select(STAFF_SELECT), 'read staff'),
    must(sb.from('clients').select('id, client_id, axiscare_client_id').not('axiscare_client_id', 'is', null), 'read clients'),
    must(sb.from('payroll_run_lines').select('*').eq('run_id', runId), "read the run's lines"),
    must(sb.from('staff_pay_rates').select('staff_id, pay_rate, pay_type, effective_from'), 'read pay-rate history'),
    must(sb.from('service_catalog').select('code, axiscare_service_code, axiscare_service_aliases, payable'), 'read the service catalog'),
  ]);
  return { staffRows: staffRows || [], clientRows: clientRows || [], existing: existing || [], rateHistory: rateHistory || [], catalog: catalog || [] };
};

/**
 * Rebuild a draft run's automatic lines from the week's visits, each paid at
 * the rate in force on its date. Manual lines stay. A finalized run is refused.
 *
 * @param {object}   sb
 * @param {object}   run      the payroll_runs row
 * @param {object[]} visits   getVisitsByDateRange(..., { mode: 'payroll' }) results
 * @param {object}  [opts]
 * @param {boolean} [opts.dryRun]     build and report, write nothing
 * @param {string}  [opts.source]     'app' | 'scheduled' | 'backfill', saved as pull_source
 */
export const pullPayrollRun = async (sb, run, visits, { dryRun = false, source = 'app' } = {}) => {
  if (run.status === 'finalized') throw new Error('This run is finalized. Re-open it before pulling again.');
  const { staffRows, clientRows, existing, rateHistory, catalog } = await loadPullInputs(sb, run.id);
  const manualLines = existing.filter(l => l.is_manual);
  const built = buildPayrollLines({ run, visits, staffRows, clientRows, manualLines, rateHistory, catalog });
  const totals = payrollTotals([...built.lines, ...manualLines]);
  const before = payrollTotals(existing);
  const pullExceptions = { ...built.exceptions, pulled_visits: visits.length };
  if (dryRun) return { ...built, totals, before, existing };

  await must(sb.from('payroll_run_lines').delete().eq('run_id', run.id).eq('is_manual', false), 'clear the old lines');
  if (built.lines.length) await must(sb.from('payroll_run_lines').insert(built.lines), 'write the new lines');
  await must(sb.from('payroll_runs').update({
    ...totals,
    pulled_at: new Date().toISOString(),
    pull_source: source,
    pull_exceptions: pullExceptions,
    updated_at: new Date().toISOString(),
  }).eq('id', run.id), 'update the run totals');
  return { ...built, totals, before, existing };
};

/** The run's stored totals, recomputed from its lines after an edit. */
export const saveRunTotals = async (sb, runId, lines) =>
  must(sb.from('payroll_runs').update({ ...payrollTotals(lines), updated_at: new Date().toISOString() }).eq('id', runId), 'update the run totals');
